import { z } from 'zod';
import { locationSnapshotSchema } from './location.js';
import { calendarDateSchema, canonicalJsonStringify, instantSchema, noteSchema, nonnegativeSafeIntegerSchema, recordedMetresSchema, revisionSchema, uuidSchema, zonedMomentSchema, } from './primitives.js';
import { routeSnapshotSchema } from './route.js';
import { routeDraftEndpointLocationSnapshot, routeDraftSnapshotSchema } from './route-draft.js';
import { datasetVersionsSchema, revisionMetadataSchema } from './versioning.js';
import { vehicleSnapshotSchema } from './vehicle.js';
export const workTypeSchema = z.enum([
    'passenger',
    'freight',
    'shunting',
    'light_engine',
    'service_run',
    'other',
]);
export const crewRoleSchema = z.enum([
    'driver',
    'second_driver',
    'instructor',
    'transport',
    'other',
]);
export const tripSegmentStatusSchema = z.enum(['draft', 'complete', 'cancelled', 'partial']);
export const shiftSchema = z.strictObject({
    shift_id: uuidSchema,
    service_date: calendarDateSchema,
    actual_start: zonedMomentSchema.nullable(),
    actual_end: zonedMomentSchema.nullable(),
    home_base: z.string().min(1).max(256).nullable(),
    shift_type: z.string().min(1).max(128).nullable(),
    status: z.enum(['open', 'closed']),
    note: noteSchema,
    segment_order: z.array(uuidSchema).max(1_000),
    metadata: revisionMetadataSchema,
});
export const tripSegmentSchema = z
    .strictObject({
    trip_segment_id: uuidSchema,
    shift_id: uuidSchema,
    order: nonnegativeSafeIntegerSchema,
    actual_start: zonedMomentSchema.nullable(),
    actual_end: zonedMomentSchema.nullable(),
    origin: locationSnapshotSchema.nullable(),
    destination: locationSnapshotSchema.nullable(),
    planned_destination: locationSnapshotSchema.nullable(),
    early_termination_reason: z.string().min(1).max(1_000).nullable(),
    route_snapshot: routeSnapshotSchema.nullable(),
    /** Missing only on legacy records written before status-specific route snapshots existed. */
    route_draft_snapshot: routeDraftSnapshotSchema.nullable().exactOptional(),
    train_or_service_number: z.string().min(1).max(128).nullable(),
    work_type: workTypeSchema.nullable(),
    role: crewRoleSchema.nullable(),
    operator: z.string().min(1).max(256).nullable(),
    vehicles: z.array(vehicleSnapshotSchema).max(16),
    distance_metres: recordedMetresSchema.nullable(),
    note: noteSchema,
    tags: z.array(z.string().min(1).max(64)).max(64),
    status: tripSegmentStatusSchema,
    data_versions: datasetVersionsSchema,
    metadata: revisionMetadataSchema,
})
    .superRefine((value, context) => {
    const performanceRecord = value.status === 'complete' || value.status === 'partial';
    const routeDraftSnapshot = value.route_draft_snapshot ?? null;
    const hasStatusSpecificRouteSnapshot = Object.hasOwn(value, 'route_draft_snapshot');
    if (performanceRecord &&
        (value.origin === null || value.destination === null || value.route_snapshot === null)) {
        context.addIssue({
            code: 'custom',
            message: 'complete and partial segments require origin, destination, and an explicitly confirmed route snapshot',
        });
    }
    if (value.route_snapshot !== null && routeDraftSnapshot !== null) {
        context.addIssue({
            code: 'custom',
            path: ['route_draft_snapshot'],
            message: 'a segment cannot retain confirmed and unconfirmed route snapshots together',
        });
    }
    if (hasStatusSpecificRouteSnapshot) {
        if (performanceRecord && routeDraftSnapshot !== null) {
            context.addIssue({
                code: 'custom',
                path: ['route_draft_snapshot'],
                message: 'complete and partial segments cannot retain an unconfirmed route draft',
            });
        }
        if (!performanceRecord && value.route_snapshot !== null) {
            context.addIssue({
                code: 'custom',
                path: ['route_snapshot'],
                message: 'draft and cancelled segments cannot retain confirmation or route learning',
            });
        }
    }
    if (routeDraftSnapshot !== null) {
        const draftOrigin = routeDraftEndpointLocationSnapshot(routeDraftSnapshot.origin);
        const draftDestination = routeDraftSnapshot.destination === null
            ? null
            : routeDraftEndpointLocationSnapshot(routeDraftSnapshot.destination);
        if (canonicalJsonStringify(draftOrigin) !==
            canonicalJsonStringify(value.origin)) {
            context.addIssue({
                code: 'custom',
                path: ['origin'],
                message: 'segment origin must match the valid unconfirmed route endpoint projection',
            });
        }
        if (canonicalJsonStringify(draftDestination) !==
            canonicalJsonStringify(value.destination)) {
            context.addIssue({
                code: 'custom',
                path: ['destination'],
                message: 'segment destination must match the valid unconfirmed route endpoint projection',
            });
        }
        if (routeDraftSnapshot.route_seed !== null) {
            if (!sameArtifact(value.data_versions.catalog, routeDraftSnapshot.route_seed.catalog_version)) {
                context.addIssue({
                    code: 'custom',
                    path: ['data_versions', 'catalog'],
                    message: 'segment and route draft must retain the same catalog version',
                });
            }
            if (!sameArtifact(value.data_versions.graph, routeDraftSnapshot.route_seed.graph_version)) {
                context.addIssue({
                    code: 'custom',
                    path: ['data_versions', 'graph'],
                    message: 'segment and route draft must retain the same graph version',
                });
            }
            if (value.data_versions.route_scoring_version !==
                routeDraftSnapshot.route_seed.route_scoring_version) {
                context.addIssue({
                    code: 'custom',
                    path: ['data_versions', 'route_scoring_version'],
                    message: 'segment and route draft must retain the same scoring version',
                });
            }
        }
    }
    if (value.route_snapshot) {
        if (value.origin === null ||
            canonicalJsonStringify(value.origin) !==
                canonicalJsonStringify(value.route_snapshot.origin)) {
            context.addIssue({
                code: 'custom',
                path: ['origin'],
                message: 'origin must exactly match the immutable route location snapshot',
            });
        }
        if (value.destination === null ||
            canonicalJsonStringify(value.destination) !==
                canonicalJsonStringify(value.route_snapshot.destination)) {
            context.addIssue({
                code: 'custom',
                path: ['destination'],
                message: 'destination must exactly match the immutable route location snapshot',
            });
        }
        if (value.distance_metres !== value.route_snapshot.distance.metres) {
            context.addIssue({
                code: 'custom',
                path: ['distance_metres'],
                message: 'segment distance must exactly match its immutable route snapshot',
            });
        }
        if (!sameArtifact(value.data_versions.catalog, value.route_snapshot.catalog_version)) {
            context.addIssue({
                code: 'custom',
                path: ['data_versions', 'catalog'],
                message: 'segment and route snapshot must retain the same catalog version',
            });
        }
        if (!sameArtifact(value.data_versions.graph, value.route_snapshot.graph_version)) {
            context.addIssue({
                code: 'custom',
                path: ['data_versions', 'graph'],
                message: 'segment and route snapshot must retain the same graph version',
            });
        }
        if (value.data_versions.route_scoring_version !== value.route_snapshot.route_scoring_version) {
            context.addIssue({
                code: 'custom',
                path: ['data_versions', 'route_scoring_version'],
                message: 'segment and route snapshot must retain the same scoring version',
            });
        }
    }
    else if (value.distance_metres !== null) {
        context.addIssue({
            code: 'custom',
            path: ['distance_metres'],
            message: 'distance without a route snapshot is not allowed',
        });
    }
    if (value.status === 'partial') {
        if (value.planned_destination === null || value.early_termination_reason === null) {
            context.addIssue({
                code: 'custom',
                message: 'partial segment requires planned_destination and early_termination_reason',
            });
        }
        if (value.planned_destination?.location_id === value.destination?.location_id) {
            context.addIssue({
                code: 'custom',
                path: ['planned_destination'],
                message: 'planned destination of a partial segment must differ from actual destination',
            });
        }
    }
    else if (value.planned_destination !== null || value.early_termination_reason !== null) {
        context.addIssue({
            code: 'custom',
            message: 'planned_destination and early_termination_reason are reserved for partial segments',
        });
    }
    const vehicleIds = value.vehicles.map((vehicle) => vehicle.vehicle_id);
    if (new Set(vehicleIds).size !== vehicleIds.length) {
        context.addIssue({
            code: 'custom',
            path: ['vehicles'],
            message: 'vehicle snapshots must be unique within a segment',
        });
    }
    if (new Set(value.tags).size !== value.tags.length) {
        context.addIssue({
            code: 'custom',
            path: ['tags'],
            message: 'tags must be unique and stored in semantic order',
        });
    }
});
export const shiftAggregateSchema = z
    .strictObject({
    contract_version: z.literal('shift_aggregate.v1'),
    dataset_epoch: uuidSchema,
    aggregate_revision: revisionSchema,
    shift: shiftSchema,
    segments: z.array(tripSegmentSchema).max(1_000),
})
    .superRefine((value, context) => {
    const segmentIds = value.segments.map((segment) => segment.trip_segment_id);
    if (new Set(segmentIds).size !== segmentIds.length) {
        context.addIssue({
            code: 'custom',
            path: ['segments'],
            message: 'segment IDs must be unique',
        });
    }
    value.segments.forEach((segment, index) => {
        if (segment.shift_id !== value.shift.shift_id) {
            context.addIssue({
                code: 'custom',
                path: ['segments', index, 'shift_id'],
                message: 'segment must belong to aggregate shift',
            });
        }
        if (segment.order !== index) {
            context.addIssue({
                code: 'custom',
                path: ['segments', index, 'order'],
                message: 'segments must be contiguous, zero-based, and stored in their semantic order',
            });
        }
        if (segment.metadata.revision > value.aggregate_revision) {
            context.addIssue({
                code: 'custom',
                path: ['segments', index, 'metadata', 'revision'],
                message: 'entity revision cannot exceed aggregate revision',
            });
        }
        if (segment.metadata.tombstone &&
            segment.metadata.tombstone.dataset_epoch !== value.dataset_epoch) {
            context.addIssue({
                code: 'custom',
                path: ['segments', index, 'metadata', 'tombstone', 'dataset_epoch'],
                message: 'segment tombstone must belong to aggregate dataset_epoch',
            });
        }
    });
    if (value.shift.metadata.revision > value.aggregate_revision) {
        context.addIssue({
            code: 'custom',
            path: ['shift', 'metadata', 'revision'],
            message: 'shift revision cannot exceed aggregate revision',
        });
    }
    if (value.shift.segment_order.length !== segmentIds.length ||
        value.shift.segment_order.some((id, index) => id !== segmentIds[index])) {
        context.addIssue({
            code: 'custom',
            path: ['shift', 'segment_order'],
            message: 'shift segment_order must exactly match aggregate segment order',
        });
    }
    const aggregateTombstone = value.shift.metadata.tombstone;
    if (aggregateTombstone) {
        if (aggregateTombstone.dataset_epoch !== value.dataset_epoch) {
            context.addIssue({
                code: 'custom',
                path: ['shift', 'metadata', 'tombstone', 'dataset_epoch'],
                message: 'shift tombstone must belong to aggregate dataset_epoch',
            });
        }
        value.segments.forEach((segment, index) => {
            const segmentTombstone = segment.metadata.tombstone;
            if (segmentTombstone === null ||
                canonicalJsonStringify(segmentTombstone) !==
                    canonicalJsonStringify(aggregateTombstone)) {
                context.addIssue({
                    code: 'custom',
                    path: ['segments', index, 'metadata', 'tombstone'],
                    message: 'atomic aggregate deletion requires the exact same tombstone on every segment',
                });
            }
        });
    }
});
export const tripRevisionSchema = z
    .strictObject({
    trip_revision_id: uuidSchema,
    shift_id: uuidSchema,
    trip_segment_id: uuidSchema,
    dataset_epoch: uuidSchema,
    change_kind: z.enum(['create', 'edit', 'soft_delete', 'restore', 'split', 'merge']),
    previous_revision: revisionSchema.nullable(),
    resulting_revision: revisionSchema,
    recorded_at: instantSchema,
    actor_device_id: uuidSchema,
    before: tripSegmentSchema.nullable(),
    after: tripSegmentSchema.nullable(),
})
    .superRefine((value, context) => {
    if (value.before === null && value.after === null) {
        context.addIssue({ code: 'custom', message: 'revision must retain before or after state' });
    }
    if (value.before && value.before.trip_segment_id !== value.trip_segment_id) {
        context.addIssue({
            code: 'custom',
            path: ['before', 'trip_segment_id'],
            message: 'before snapshot must match revision entity',
        });
    }
    if (value.before && value.before.shift_id !== value.shift_id) {
        context.addIssue({
            code: 'custom',
            path: ['before', 'shift_id'],
            message: 'before snapshot must belong to the revision shift',
        });
    }
    if (value.after && value.after.trip_segment_id !== value.trip_segment_id) {
        context.addIssue({
            code: 'custom',
            path: ['after', 'trip_segment_id'],
            message: 'after snapshot must match revision entity',
        });
    }
    if (value.after && value.after.shift_id !== value.shift_id) {
        context.addIssue({
            code: 'custom',
            path: ['after', 'shift_id'],
            message: 'after snapshot must belong to the revision shift',
        });
    }
    if (value.previous_revision === null) {
        if (value.change_kind !== 'create' || value.before !== null) {
            context.addIssue({
                code: 'custom',
                path: ['previous_revision'],
                message: 'only create may omit previous_revision and before state',
            });
        }
    }
    else if (value.resulting_revision !== value.previous_revision + 1) {
        context.addIssue({
            code: 'custom',
            path: ['resulting_revision'],
            message: 'revision transition must increment exactly once',
        });
    }
    if (value.previous_revision !== null &&
        value.before?.metadata.revision !== value.previous_revision) {
        context.addIssue({
            code: 'custom',
            path: ['before', 'metadata', 'revision'],
            message: 'before snapshot must carry previous_revision',
        });
    }
    if (value.after === null) {
        context.addIssue({
            code: 'custom',
            path: ['after'],
            message: 'soft-delete and every other revision retain an after snapshot',
        });
    }
    if (value.after && value.after.metadata.revision !== value.resulting_revision) {
        context.addIssue({
            code: 'custom',
            path: ['after', 'metadata', 'revision'],
            message: 'after snapshot must carry resulting_revision',
        });
    }
    if (value.change_kind === 'soft_delete' && value.after?.metadata.tombstone === null) {
        context.addIssue({
            code: 'custom',
            path: ['after', 'metadata', 'tombstone'],
            message: 'soft_delete revision must retain its tombstone state',
        });
    }
    if (value.change_kind === 'restore' &&
        (value.before?.metadata.tombstone === null || value.after?.metadata.tombstone !== null)) {
        context.addIssue({
            code: 'custom',
            message: 'restore revision must transition from tombstoned to live state',
        });
    }
});
export function aggregateStructureKey(aggregateInput) {
    const aggregate = shiftAggregateSchema.parse(aggregateInput);
    return canonicalJsonStringify({
        aggregate_revision: aggregate.aggregate_revision,
        contract_version: aggregate.contract_version,
        dataset_epoch: aggregate.dataset_epoch,
        shift_id: aggregate.shift.shift_id,
        shift_revision: aggregate.shift.metadata.revision,
        segments: aggregate.segments.map((segment) => ({
            order: segment.order,
            revision: segment.metadata.revision,
            trip_segment_id: segment.trip_segment_id,
        })),
    });
}
export function aggregateCanonicalJson(aggregateInput) {
    return canonicalJsonStringify(shiftAggregateSchema.parse(aggregateInput));
}
export function isSegmentIncludedInPerformanceStats(segmentInput) {
    const segment = tripSegmentSchema.parse(segmentInput);
    return (segment.metadata.tombstone === null &&
        (segment.status === 'complete' || segment.status === 'partial'));
}
function sameArtifact(left, right) {
    return left === null
        ? right === null
        : right !== null && left.sha256 === right.sha256 && left.version === right.version;
}
//# sourceMappingURL=shift.js.map